import React, { useEffect, useState } from 'react';
import { questionAPI } from '../../../api';

type CountMap = Record<string, number>;
type SortMode = 'name' | 'desc' | 'asc';

const EXAM_TYPES: Array<'필기' | '실기'> = ['필기', '실기'];
const NO_TYPE = '(유형 미지정)';

const SORT_LABEL: Record<SortMode, string> = {
  name: '🔤 가나다순',
  desc: '⬇ 많은 순',
  asc: '⬆ 적은 순',
};
const SORT_NEXT: Record<SortMode, SortMode> = {
  name: 'desc',
  desc: 'asc',
  asc: 'name',
};

const AdminTypeStats: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [subjectCount, setSubjectCount] = useState(0);
  const [stats, setStats] = useState<Record<string, CountMap>>({ 필기: {}, 실기: {} });
  const [sortMode, setSortMode] = useState<SortMode>('name'); // 필기/실기 공통 정렬 (기본: 가나다순)

  // 종목 × 필기/실기 전체 문제를 불러와 q_type 기준으로 집계
  const loadStats = async () => {
    setLoading(true);
    try {
      const subRes = await questionAPI.adminSubjects();
      const subjects: any[] = Array.isArray(subRes.data) ? subRes.data : [];
      setSubjectCount(subjects.length);

      const result: Record<string, CountMap> = { 필기: {}, 실기: {} };
      const tasks: Promise<void>[] = [];

      subjects.forEach((s) => {
        EXAM_TYPES.forEach((et) => {
          tasks.push(
            questionAPI.list({ subject_id: s.id, exam_type: et })
              .then((res) => {
                const list: any[] = Array.isArray(res.data) ? res.data : [];
                list.forEach((q) => {
                  const raw = q.q_type != null ? String(q.q_type).trim() : '';
                  const t = raw ? raw : NO_TYPE;
                  result[et][t] = (result[et][t] || 0) + 1;
                });
              })
              .catch(() => {}) // 일부 종목 실패해도 나머지 계속 집계
          );
        });
      });

      await Promise.all(tasks);
      setStats(result);
      setLoaded(true);
    } catch (e) {
      alert('집계 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 섹션 진입 시 자동 집계 (버튼으로 재집계 가능)
  useEffect(() => {
    loadStats(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderGrid = (examType: '필기' | '실기') => {
    const map = stats[examType] || {};
    const total = Object.values(map).reduce((sum, c) => sum + c, 0);
    const rows = Object.entries(map).sort((a, b) => {
      if (sortMode === 'name') return a[0].localeCompare(b[0], 'ko');
      return sortMode === 'desc' ? b[1] - a[1] : a[1] - b[1];
    });
    const icon = examType === '필기' ? '📘' : '📙';

    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          {icon} {examType} 유형별 문제 수{' '}
          <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>
            · 유형 {rows.length}종 · 총 {total.toLocaleString()}문제
          </span>
        </div>
        <div className="card-body">
          {rows.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              집계된 {examType} 문제가 없습니다.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
            }}>
              {rows.map(([type, count]) => {
                const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
                const isNoType = type === NO_TYPE;
                return (
                  <div key={type} style={{
                    background: isNoType ? '#fff7ed' : 'var(--gray-50)',
                    border: `1.5px solid ${isNoType ? '#fdba74' : 'var(--border)'}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    minWidth: 0,
                  }}>
                    <div style={{
                      fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)',
                      lineHeight: 1.3, wordBreak: 'break-all',
                    }}>{type}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)' }}>
                        {count.toLocaleString()}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>문제 · {pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <span className="icon">📈</span>
          유형 통계
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={() => setSortMode(m => SORT_NEXT[m])}
            disabled={loading}
            title="클릭 시 순환 · 필기/실기 동일 적용">
            {SORT_LABEL[sortMode]}
          </button>
          <button className="btn btn-primary" onClick={loadStats} disabled={loading}>
            {loading ? '집계 중…' : '🔄 새로고침'}
          </button>
        </div>
      </div>

      {!loaded && loading && (
        <div className="loading-spinner"><div className="spinner" /></div>
      )}

      {loaded && (
        <>
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            📚 집계 종목 {subjectCount}개 · 필기/실기 전체 문제 기준 · 유형(q_type) 값 기준 집계
            (유형 미입력은 "{NO_TYPE}"로 표시)
          </div>
          {renderGrid('필기')}
          {renderGrid('실기')}
        </>
      )}
    </div>
  );
};

export default AdminTypeStats;
